/**
 * Räume (27.10.2026, Timo): Die Navigation ordnet die App in vier Räume —
 * **Ich**, **Haushalt** (wer hier wohnt), **Familie** (wer nicht hier wohnt,
 * aber mithilft) und **Community**. Je Raum höchstens fünf Reiter, links
 * daneben der Raum-Knopf: sechs gleich breite Plätze wie bisher.
 *
 * Reine Oberfläche: kein Datenmodell, kein Server. Die Bildschirme sind
 * dieselben wie vorher — ein Raum entscheidet nur, welche davon in der Leiste
 * stehen und wie „Planen“ vorgefiltert ist. Die Regeln stehen hier ohne DOM,
 * damit sie prüfbar sind.
 */

import type { Module } from '@kraftwerk/shared';

export const ROOMS = ['me', 'household', 'family', 'community'] as const;
export type Room = (typeof ROOMS)[number];

export type RoomTab =
  | 'today'
  | 'training'
  | 'nutrition'
  | 'journal'
  | 'plan'
  | 'home'
  | 'meals'
  | 'shopping'
  | 'costs'
  | 'pocket'
  | 'members'
  | 'family'
  | 'community';

/** Raum-Knopf plus fünf Reiter — mehr passen bei 375 px nicht. */
export const MAX_ROOM_TABS = 5;

export function isRoom(value: unknown): value is Room {
  return typeof value === 'string' && (ROOMS as readonly string[]).includes(value);
}

/**
 * Die Reiter eines Raums nach Modulen (E15) und Rolle (E16).
 *
 * - **Ich:** Heute (immer), Training, Ernährung, Tagebuch, Planen.
 * - **Haushalt** gibt es nur mit dem Modul Haushalt (wie vorher der Reiter):
 *   Heute im Haushalt, Essen, Einkauf, Kosten (Kinder: Taschengeld — Kosten
 *   sind Sache der Erwachsenen), Planen.
 *
 * Essen (27.10.2026) kam als fünfter Reiter dazu; sechs passen nicht.
 * **Mitglieder** steht seitdem nicht mehr in der Leiste, sondern als Knopf
 * auf „Heute im Haushalt“ (`HIDDEN_ROOM_TABS`): Mitglieder ist Einrichtung
 * (einladen, Kinder anlegen, Stufen, Standort freigeben) und wird selten
 * gebraucht — Essen, Einkauf und Planen jede Woche. Essen steht neben
 * Einkauf, weil der Plan die Liste füllt. Der Sprung aus einer Mitteilung
 * (Standort) führt weiter direkt dorthin.
 * - **Familie** und **Community** sind vorerst ehrliche Startseiten — nur
 *   für Erwachsene: Wen ein Kind einlädt oder was es öffentlich teilt,
 *   entscheiden die Eltern, und beides gibt es noch nicht.
 */
export function roomTabs(room: Room, modules: readonly Module[], child: boolean): RoomTab[] {
  const on = (module: Module) => modules.includes(module);
  if (room === 'me') {
    const tabs: RoomTab[] = ['today'];
    if (on('training')) tabs.push('training');
    if (on('nutrition')) tabs.push('nutrition');
    if (on('journal')) tabs.push('journal');
    if (on('planning')) tabs.push('plan');
    return tabs;
  }
  if (room === 'household') {
    if (!on('household')) return [];
    const tabs: RoomTab[] = ['home', 'meals', 'shopping', child ? 'pocket' : 'costs'];
    if (on('planning')) tabs.push('plan');
    return tabs;
  }
  if (child) return [];
  return room === 'family' ? ['family'] : ['community'];
}

/**
 * Seiten eines Raums, die nicht in der Leiste stehen, aber erreichbar sind —
 * mit dem Reiter, unter dem sie hängen (der leuchtet dann in der Leiste).
 */
export const HIDDEN_ROOM_TABS: Partial<Record<Room, Partial<Record<RoomTab, RoomTab>>>> = {
  household: { members: 'home' },
};

/** Alle Seiten eines Raums: die Reiter der Leiste und die dahinter. */
export function roomPages(room: Room, modules: readonly Module[], child: boolean): RoomTab[] {
  const tabs = roomTabs(room, modules, child);
  const hidden = Object.entries(HIDDEN_ROOM_TABS[room] ?? {})
    .filter(([, parent]) => tabs.includes(parent))
    .map(([page]) => page as RoomTab);
  return [...tabs, ...hidden];
}

/** Welcher Reiter der Leiste für eine Seite leuchtet. */
export function barTabFor(room: Room, tab: RoomTab): RoomTab {
  return HIDDEN_ROOM_TABS[room]?.[tab] ?? tab;
}

/** Räume, die im Blatt stehen: nur mit mindestens einem Reiter. */
export function roomsFor(modules: readonly Module[], child: boolean): Room[] {
  return ROOMS.filter((room) => roomTabs(room, modules, child).length > 0);
}

/**
 * Was gilt, wenn der gemerkte Raum oder Reiter nicht (mehr) da ist — etwa
 * weil ein Modul abgeschaltet wurde: Raum Ich, erster Reiter.
 */
export function resolvePlace(
  wanted: { room: Room; tab: RoomTab },
  modules: readonly Module[],
  child: boolean,
): { room: Room; tab: RoomTab } {
  const rooms = roomsFor(modules, child);
  const room = rooms.includes(wanted.room) ? wanted.room : 'me';
  const tabs = roomPages(room, modules, child);
  return { room, tab: tabs.includes(wanted.tab) ? wanted.tab : tabs[0]! };
}

// --- Planen über alle Räume ------------------------------------------------------

/**
 * Planen ist **eine** Planung über alle Räume (kein zweiter Kalender). Der
 * Raum filtert nur vor — im Raum Ich „meins“, im Haushalt „unseres“ —, und
 * ein sichtbarer Umschalter zeigt auf Wunsch alles.
 */
export const PLAN_SCOPES = ['mine', 'ours', 'all'] as const;
export type PlanScope = (typeof PLAN_SCOPES)[number];

export function defaultScope(room: Room): PlanScope {
  return room === 'household' ? 'ours' : 'mine';
}

/**
 * Gehört ein Kalendereintrag in die Auswahl?
 *
 * - **unseres:** alles Geteilte des Haushalts (`householdId` gesetzt).
 * - **meins:** Privates (eigene Termine, Abos, Training, private Aufgaben)
 *   und Geteiltes, das mich betrifft — ein Haushaltstermin „für mich“, eine
 *   mir zugewiesene Aufgabe (der Kalender führt ohnehin nur solche).
 */
export function inPlanScope(
  item: { kind: string; householdId: string | null; forUserIds: readonly string[] },
  scope: PlanScope,
  me: string | null,
): boolean {
  if (scope === 'all') return true;
  if (scope === 'ours') return item.householdId !== null;
  if (item.householdId === null || item.kind === 'task') return true;
  return me !== null && item.forUserIds.includes(me);
}

/** Aufgabenlisten: meins = private, unseres = geteilte. */
export function listInPlanScope(list: { householdId: string | null }, scope: PlanScope): boolean {
  if (scope === 'all') return true;
  return scope === 'ours' ? list.householdId !== null : list.householdId === null;
}

// --- Gemerkter Raum je Gerät -----------------------------------------------------

/**
 * Der zuletzt gewählte Raum, je Gerät — `localStorage`, weil er schon vor der
 * ersten Abfrage an die Datenbank feststehen muss (sonst blinkte beim Start
 * Raum Ich auf). Ohne Speicher (privates Fenster) gilt Ich.
 */
export const ROOM_STORAGE_KEY = 'kraftwerk:room';

export function readRoom(): Room {
  try {
    const stored = window.localStorage.getItem(ROOM_STORAGE_KEY);
    return isRoom(stored) ? stored : 'me';
  } catch {
    return 'me';
  }
}

export function writeRoom(room: Room): void {
  try {
    window.localStorage.setItem(ROOM_STORAGE_KEY, room);
  } catch {
    // Kein Speicher: Der Raum gilt bis zum Schließen der App.
  }
}
